import { useEffect, useLayoutEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { SelectIssueOutcome } from "@/stores/pm/IssuesStore";
import { logger } from "@/utils/logger";

// One toast however many times the failure is reported (a StrictMode re-run, a reload).
const FAILED_TOAST_ID = "issue-deep-link-failed";

interface DeepLinkStore {
  selectIssue(issueId: number, options?: { quiet?: boolean }): Promise<SelectIssueOutcome>;
}

/**
 * Open the issue a `?selected=` deep link names once the page's first load
 * lands, then take the parameter out of the URL whatever the outcome. The
 * link is one attempt, not a standing state: left in the URL it would keep
 * the filters bypassed for the rest of the visit (`usePersistedIssueFilters`
 * reads its presence), and following the same link again would change
 * nothing and so do nothing. Consumed, it can be followed again, and that
 * retries.
 *
 * A link that could not be opened is reported here, in a toast, and only
 * here: `selectIssue` runs `quiet`, so it leaves the store's error (the
 * page's banner) alone. The banner is the wrong home for it anyway, since the
 * next list reload clears it and would leave the failed link reported
 * nowhere.
 *
 * Each link is opened once, however often the list reloads: `hasLoaded`
 * cycles on every filter, search or project change.
 *
 * A `?selected=` that names no issue at all (`issueId` null while the URL
 * carries the parameter: empty, truncated, hand-edited, out of range) gets
 * the toast and is consumed the same way, without ever reaching the store.
 *
 * `selectIssue` fetches by id directly, so a deep-linked issue opens even if
 * it is outside the list's filters.
 */
export function useOpenIssueDeepLink(
  issueId: number | null,
  hasLoaded: boolean,
  issuesStore: DeepLinkStore
) {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  // Present-but-empty (`?selected=`) is a truncated link, not an absent one.
  const rawLink = searchParams.has("selected") ? searchParams.get("selected") : null;
  const malformed = issueId === null ? rawLink : null;
  // Refs, not deps: the setter's identity follows the URL, and the strip
  // must not be tied to an effect run that a re-render can cancel. Written
  // on commit, never during render, so an abandoned render cannot leave a
  // guard pointing at a link that never reached the screen; a layout effect
  // so they are current before any effect below reads them.
  const setParamsRef = useRef(setSearchParams);
  const failedMessage = useRef(t.pm.issues.deepLinkFailed);
  const unavailableMessage = useRef(t.pm.issues.deepLinkUnavailable);
  const currentId = useRef(issueId);
  useLayoutEffect(() => {
    setParamsRef.current = setSearchParams;
    failedMessage.current = t.pm.issues.deepLinkFailed;
    unavailableMessage.current = t.pm.issues.deepLinkUnavailable;
    currentId.current = issueId;
  });
  const handled = useRef<number | null>(null);
  // The same id can be attempted twice (stripped and followed again); only
  // the latest attempt may report or strip.
  const latestAttempt = useRef(0);
  // Forget the last link as soon as the URL moves off it, loaded or not: a
  // link stripped and followed again during a reload is a new attempt.
  useEffect(() => {
    if (handled.current !== issueId) handled.current = null;
  }, [issueId]);
  // The strip navigates relative to this page's URL; once the page is gone
  // it would pull the user back to it.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const strip = () =>
    setParamsRef.current(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete("selected");
        return next;
      },
      { replace: true }
    );

  useEffect(() => {
    if (!hasLoaded || malformed === null) return;
    toast.error(failedMessage.current, { id: FAILED_TOAST_ID });
    strip();
  }, [hasLoaded, malformed]);

  useEffect(() => {
    if (!hasLoaded || issueId === handled.current) return;
    handled.current = issueId;
    if (issueId === null) return;

    const attempt = ++latestAttempt.current;
    // Not once the page is gone, nor for a link the URL has moved on from or
    // a later attempt has taken over.
    const current = () =>
      mounted.current && currentId.current === issueId && latestAttempt.current === attempt;
    const fail = () => {
      if (current()) toast.error(failedMessage.current, { id: FAILED_TOAST_ID });
    };
    // Every outcome consumes the link; a "superseded" one has the user's own
    // pick on screen.
    const consume = () => {
      if (current()) strip();
    };

    Promise.resolve(issuesStore.selectIssue(issueId, { quiet: true })).then(
      (outcome) => {
        if (outcome === "failed") fail();
        // The read failed, not the issue: say so, without "deleted".
        else if (outcome === "unavailable" && current())
          toast.error(unavailableMessage.current, { id: FAILED_TOAST_ID });
        consume();
      },
      (error: unknown) => {
        // selectIssue reports its own failures; a throw out of it is unexpected.
        void logger.error(
          "ui",
          "issue_deep_link_open",
          "Failed to open deep-linked issue",
          error instanceof Error ? error : new Error(String(error)),
          { issueId }
        );
        fail();
        consume();
      }
    );
  }, [hasLoaded, issueId, issuesStore]);
}
